import { useState } from "react";
import { Plus, X, Trash, TreeStructure } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

export const nid = () => Math.random().toString(36).slice(2, 10);

export const FIELD_TYPES = ["number", "text", "select", "multi-select", "boolean", "date"];
export const COLORS = ["var(--c-accent)", "var(--c-green)", "var(--c-amber)", "var(--c-red)", "var(--c-muted)"];
export const EMPTY_FIELD = { id: "", label: "", type: "number", unit: "", required: false, optionsText: "", default: "", min: "", max: "", help: "", ai_hint: "" };

export function TagInput({ tags, onChange, placeholder, testid }) {
  const [v, setV] = useState("");
  const add = () => {
    const t = v.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setV("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input data-testid={testid} value={v} onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
        <Button type="button" data-testid={`${testid}-add`} onClick={add} size="sm"
          className="bg-[var(--c-border)] hover:bg-[var(--c-border)]/70 text-[var(--c-text)] h-8 rounded-sm"><Plus size={13} /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[var(--c-accent)]/10 border border-[var(--c-accent)]/40 text-[var(--c-text)] text-[10px]">
            {t}
            <button type="button" onClick={() => onChange(tags.filter((x) => x !== t))} className="text-[var(--c-muted)] hover:text-[var(--c-red)]"><X size={9} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function updateAtPath(nodes, path, fn) {
  if (path.length === 1) return nodes.map((n, i) => (i === path[0] ? fn(n) : n));
  return nodes.map((n, i) => (i === path[0] ? { ...n, children: updateAtPath(n.children || [], path.slice(1), fn) } : n));
}

function removeAtPath(nodes, path) {
  if (path.length === 1) return nodes.filter((_, i) => i !== path[0]);
  return nodes.map((n, i) => (i === path[0] ? { ...n, children: removeAtPath(n.children || [], path.slice(1)) } : n));
}

function flattenTree(nodes, path = [], depth = 0, out = []) {
  nodes.forEach((n, i) => {
    const p = [...path, i];
    out.push({ node: n, path: p, depth });
    flattenTree(n.children || [], p, depth + 1, out);
  });
  return out;
}

export function BomTree({ nodes, onChange }) {
  const rows = flattenTree(nodes);
  return (
    <div className="space-y-1.5">
      {rows.map(({ node, path, depth }) => (
        <div key={node.id} className="flex items-center gap-2" style={{ marginLeft: depth * 22 }} data-testid={`bom-node-${node.id}`}>
          <TreeStructure size={13} className="text-[var(--c-muted)] shrink-0" />
          <Input value={node.name}
            onChange={(e) => onChange(updateAtPath(nodes, path, (n) => ({ ...n, name: e.target.value })))}
            className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs max-w-72" />
          <button type="button" title="Add child item" data-testid={`bom-add-child-${node.id}`}
            onClick={() => onChange(updateAtPath(nodes, path, (n) => ({ ...n, children: [...(n.children || []), { id: nid(), name: "New Item", children: [] }] })))}
            className="p-1 text-[var(--c-muted)] hover:text-[var(--c-accent)] transition-colors duration-150"><Plus size={12} /></button>
          <button type="button" title="Remove" data-testid={`bom-remove-${node.id}`}
            onClick={() => onChange(removeAtPath(nodes, path))}
            className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150"><Trash size={12} /></button>
        </div>
      ))}
    </div>
  );
}

export function ListRow({ item, onDelete, extra, testid }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--c-border)]/60 last:border-0" data-testid={testid}>
      <span className="font-mono text-[10px] text-[var(--c-muted)] w-40 shrink-0 truncate">{item.id}</span>
      <span className="text-xs font-medium text-[var(--c-text)] w-44 shrink-0 truncate">{item.name}</span>
      <span className="text-xs text-[var(--c-muted)] flex-1 truncate">{extra}</span>
      <button type="button" onClick={onDelete} className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={13} /></button>
    </div>
  );
}
